import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

/**
 * Globale, tenant-weite Meetings (nicht projektgebunden) — auf der Timeline
 * als Linie + Icon dargestellt. Backend: /api/v1/global-meetings (projectapi,
 * global_meetings, Migration 046). Feldnamen deutsch, analog project-meetings.
 */
export interface GlobalMeeting {
  id: string;
  tenant_id?: string;
  titel: string;
  datum: string; // YYYY-MM-DD
  uhrzeit: string | null; // HH:MM[:SS]
  beschreibung: string | null;
  teilnehmer: string | null;
  ort: string | null;
  link_uri: string | null;
  created_at?: string;
  updated_at?: string;
}

const KEY = ['global-meetings'];

/** Liste aller globalen Meetings; optional auf einen Datums-Bereich gefiltert. */
export function useGlobalMeetings(params?: { from?: string; to?: string }) {
  return useQuery({
    queryKey: [...KEY, params ?? null],
    queryFn: async () => {
      try {
        return await apiClient.get('/api/v1/global-meetings', params as Record<string, unknown> | undefined);
      } catch {
        // Endpoint evtl. noch nicht deployed (Migration 046 ausstehend) — leere
        // Liste statt harter Fehler, damit die Timeline weiter rendert.
        return { success: false, data: [] };
      }
    },
    retry: false,
  });
}

export function useCreateGlobalMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/api/v1/global-meetings', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateGlobalMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`/api/v1/global-meetings/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteGlobalMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/global-meetings/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
